import type {
  SenderConfig,
  NavigateOptions,
  ChangeAppApiResponse,
} from './types';
import {
  MINITOKEN_PARAM,
  ORIGIN_CLIENT_ID_PARAM,
  USER_ID_PARAM,
} from './constants';
import { getAccessToken } from '@/shared/api/storage';
import { httpPost } from './http';

/**
 * ChangeAppSender
 *
 * Handles outbound cross-portal navigation via the IAM change-app flow.
 * Obtains a minitoken through the BFF and redirects the user to the target
 * portal with the token appended as a query parameter.
 *
 * The BFF performs the IAM call on the portal's behalf
 * (`POST /auth/change-app`), authenticated by the user's bearer token.
 *
 * ## Flow
 * 1. Calls BFF `POST /auth/change-app` with `Authorization: Bearer`
 * 2. BFF calls IAM `POST /user-identities/me/change-app?targetClientId=...`
 * 3. Redirects via `window.location.href` to target URL with minitoken param
 *
 * @example
 * ```ts
 * const sender = new ChangeAppSender({ bffBaseUrl: 'https://api.example.com' });
 *
 * await sender.navigateTo({
 *   targetClientId: 'pdt-client-id',
 *   targetUrl: 'https://plataformadatatrust.clearsale.com.br/consulta',
 * });
 * ```
 *
 * @since 1.0.0
 * @category Services/ChangeApp
 */
export class ChangeAppSender {
  private readonly bffBaseUrl: string;

  constructor(config: SenderConfig) {
    this.bffBaseUrl = config.bffBaseUrl.replace(/\/+$/, '');
  }

  /**
   * Navigate the user to the target portal via minitoken redirect
   *
   * @param options - Navigation target configuration
   * @throws {ChangeAppError} When the BFF/IAM returns an error
   */
  async navigateTo(options: NavigateOptions): Promise<void> {
    const minitoken = await this.changeApp(options);

    const url = new URL(options.targetUrl);
    url.searchParams.set(MINITOKEN_PARAM, minitoken);
    if (options.originClientId) {
      url.searchParams.set(ORIGIN_CLIENT_ID_PARAM, options.originClientId);
    }
    if (options.userId) {
      url.searchParams.set(USER_ID_PARAM, options.userId);
    }
    window.location.href = url.toString();
  }

  /**
   * Request a minitoken for the target portal
   *
   * @param options - Navigation options containing target client ID
   * @returns Minitoken string
   */
  private async changeApp(options: NavigateOptions): Promise<string> {
    const body: Record<string, unknown> = {
      targetClientId: options.targetClientId,
      ...(options.deviceId && { deviceId: options.deviceId }),
      ...(options.ip && { ip: options.ip }),
    };

    const accessToken = getAccessToken();
    const response = await httpPost<ChangeAppApiResponse>(
      `${this.bffBaseUrl}/auth/change-app`,
      body,
      accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
    );

    return response.token;
  }
}
